// Main Layout Component
import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  Typography,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Logout,
  Settings,
  AccountCircle,
  Receipt,
  PersonAdd,
  Event,
} from '@mui/icons-material';
import authService from '../services/auth';
import apiService from '../services/api';
import Navigation from './navigation/Navigation';
import SuperAdminNavigation from './superadmin/SuperAdminNavigation';
import Header from './layout/Header';
import './Layout.css';
import { useCurrency } from '../contexts/CurrencyContext';
// import BreadcrumbNavigation from './navigation/BreadcrumbNavigation';

const Layout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [quickActionsAnchorEl, setQuickActionsAnchorEl] = useState(null);
  const [userPermissions, setUserPermissions] = useState(null);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [logoLoadFailed, setLogoLoadFailed] = useState(false);
  

  // Use currency context for proper currency formatting
  const { currencySymbol } = useCurrency();
 // Custom Currency Icon Component
 const CurrencyIcon = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 24,
      height: 24,
      fontSize: '1.2rem',
      fontWeight: 'bold',
      color: 'inherit',
    }}
  >
    {currencySymbol}
  </Box>
);
  // Get user data on component mount
  useEffect(() => {
    const currentUser = authService.getUser();
    const currentTenant = authService.getTenant();
    setUser(currentUser);
    setTenant(currentTenant);
    console.log('Layout - User data:', currentUser);
  }, []);

  // Listen for tenant updates (e.g., logo upload from Settings page)
  useEffect(() => {
    const handleTenantUpdated = () => {
      const currentTenant = authService.getTenant();
      setTenant(currentTenant);
      setLogoLoadFailed(false);
    };
    window.addEventListener('tenantUpdated', handleTenantUpdated);
    return () => window.removeEventListener('tenantUpdated', handleTenantUpdated);
  }, []);
  


  // Get page title based on current path
  const getPageTitle = () => {
    const path = location.pathname;
    const pageTitles = {
      '/invoices': 'Invoices',
      '/dashboard': 'Dashboard',
      '/clients': 'Clients',
      '/appointments': 'Appointments',
      '/services': 'Services',
      '/products': 'Products',
      '/brands': 'Brands',
      '/reports': 'Reports',
      '/expenses': 'Expenses',
      '/admin': 'Admin Dashboard',
      '/users': 'Staff',
      '/roles': 'Roles',
      '/memberships': 'Memberships',
      '/memberships/clients': 'Client Memberships',
      '/profile': 'Profile',
      '/settings': 'Settings',
      // Super Admin routes
      '/superadmin/dashboard': 'Super Admin Dashboard',
      '/superadmin/tenants': 'Tenant Management',
    };
    if (path.startsWith('/superadmin/tenants/') && path.endsWith('/upload-services')) {
      return 'Upload Services';
    }
    return pageTitles[path] || 'Dashboard';
  };

  // Fetch user permissions when user data is available
  useEffect(() => {
    const fetchUserPermissions = async () => {
      if (!user?.id) return;
      
      // If user is admin or super admin, skip fetching permissions as they have all access
      if (user?.role_name?.toLowerCase() === 'admin' || user?.role_name?.toLowerCase() === 'super admin') {
        setUserPermissions(null); // Set null for admin/super admin - they have all permissions
        setPermissionsLoading(false);
        return;
      }

      try {
        setPermissionsLoading(true);
        console.log('Layout - Fetching permissions for user:', user.id);
        const response = await apiService.get(`/roles/user/${user.id}/permissions`);
        console.log('Layout - API response:', response);
        console.log('Layout - API User permissions fetched:', response.data);
        setUserPermissions(response.data);
        console.log('Layout - userPermissions state set to:', response.data);
      } catch (error) {
        console.error('Error fetching user permissions:', error);
        // Set empty permissions object as fallback
        setUserPermissions({});
        console.log('Layout - userPermissions set to empty object due to error');
      } finally {
        setPermissionsLoading(false);
        console.log('Layout - permissionsLoading set to false');
      }
    };

    fetchUserPermissions();
  }, [user]); // Only depend on user object

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleQuickActionsOpen = (event) => {
    setQuickActionsAnchorEl(event.currentTarget);
  };

  const handleQuickActionsClose = () => {
    setQuickActionsAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      navigate('/login');
    }
  };

  const handleMenuClick = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleQuickAction = (path) => {
    navigate(path);
    handleQuickActionsClose();
  };

  const drawer = (
    <Box>
      {/* Logo Section */}
      <Box className="logo-section inner-logo-section">
        <Box className="logo-container"> 
          {(() => {
            const logoFilename =
              tenant?.photo ||
              tenant?.logo ||
              user?.photo ||
              user?.logo ||
              null;

            if (logoFilename && !logoLoadFailed) {
              const src = `${apiService.baseURL}/public/tenant-logo/${encodeURIComponent(logoFilename)}`;
              return (
                <img
                  src={src}
                  alt={tenant?.name || user?.tenant_name || 'Logo'}
                  style={{ maxHeight: 48, maxWidth: '100%', objectFit: 'contain' }}
                  onError={() => setLogoLoadFailed(true)}
                />
              );
            }

            return (
              <Typography variant="h5" className="logo-text">
                {user?.tenant_name || tenant?.name || 'ClickLooks'}
              </Typography>
            );
          })()}
        </Box> 
      </Box>

      <Divider className="layout-divider" />

      {/* Enhanced Navigation */}
      {!user ? (
        <Box className="loading-container">
          <Typography variant="body2" className="loading-text">
            Loading user data...
          </Typography>
        </Box>
      ) : permissionsLoading ? (
        <Box className="loading-container">
          <Typography variant="body2" className="loading-text">
            Loading permissions...
          </Typography>
        </Box>
      ) : user?.role_name?.toLowerCase() === 'super admin' ? (
        <SuperAdminNavigation 
          onMenuClick={handleMenuClick}
          isMobile={isMobile}
        />
      ) : (
        <Navigation 
          userPermissions={userPermissions}
          onMenuClick={handleMenuClick}
          isMobile={isMobile}
        />
      )}
        {console.log('Layout - Render state:', { userPermissions, permissionsLoading, user: user?.id })}

    </Box>
  );

  return (
    <Box className="layout-container">
      {/* Enhanced Header */}
      <Header
        onDrawerToggle={handleDrawerToggle}
        onProfileMenuOpen={handleProfileMenuOpen}
        onQuickActionsOpen={handleQuickActionsOpen}
        pageTitle={getPageTitle()}
        isDarkMode={false}
        onToggleTheme={() => {}} // TODO: Implement theme toggle
      />

      {/* Quick Actions Menu */}
      <Menu
        anchorEl={quickActionsAnchorEl}
        open={Boolean(quickActionsAnchorEl)}
        onClose={handleQuickActionsClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => handleQuickAction('/dashboard/invoices/new')}>
          <Receipt className="menu-item-icon" />
          New Invoice
        </MenuItem>
        <MenuItem onClick={() => handleQuickAction('/dashboard/clients')}>
          <PersonAdd className="menu-item-icon" />
          New Client
        </MenuItem>
        <MenuItem onClick={() => handleQuickAction('/dashboard/appointments')}>
          <Event className="menu-item-icon" />
          New Appointment
        </MenuItem>
        <MenuItem onClick={() => handleQuickAction('/dashboard/expenses')}>
        <CurrencyIcon className="menu-item-icon" />
         <span style={{ marginLeft: 12 }}>New Expense</span>
        </MenuItem>
      </Menu>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => navigate('/dashboard/profile')}>
          <AccountCircle className="menu-item-icon" />
          Profile
        </MenuItem>
        {(user?.role_name?.toLowerCase() === 'admin' || user?.role_name?.toLowerCase() === 'super admin') && (
          <MenuItem onClick={() => navigate('/dashboard/settings')}>
            <Settings className="menu-item-icon" />
            Settings
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={handleLogout} className="menu-item-logout">
          <Logout className="menu-item-icon" />
          Logout
        </MenuItem>
      </Menu>

      {/* Sidebar */}
      <Box
        component="nav"
        className="layout-sidebar"
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          className="layout-mobile-drawer"
          PaperProps={{
            className: 'layout-drawer-paper'
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          className="layout-desktop-drawer"
          PaperProps={{
            className: 'layout-drawer-paper'
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        className="layout-main-content"
      >
        {/* Content Container for responsive control */}
        <Box className="layout-content-container">
          {/* Breadcrumb Navigation */}
          {/* <BreadcrumbNavigation /> */}
          
          {/* Page Content */}
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
